import { BRIEF_SCRIPTING_QUERY_KEY } from "@entities/brief/lib/constants";
import { useMutation } from "@shared/lib/hooks";
import { ToastService } from "@shared/lib/services";
import { BackednResponse } from "@shared/lib/types";
import {
  IGetScriptingResponse,
  IResolveScriptComment,
  IScriptingComment,
} from "@shared/lib/types/briefCampains";
import { useQueryClient } from "@tanstack/react-query";
import { BriefConfigService } from "../../services/briefConfigService";

export function useResolveScriptComment(scriptId: string) {
  const queryClient = useQueryClient();

  const updateCommentResolve = (
    payload: IResolveScriptComment,
    data?: BackednResponse<{ comment: IScriptingComment }>
  ) => {
    queryClient.setQueryData(
      BRIEF_SCRIPTING_QUERY_KEY(scriptId),
      (oldData: BackednResponse<IGetScriptingResponse>) => {
        if (!oldData?.data) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            script: {
              ...oldData.data.script,
              comments: (oldData.data?.script?.comments || []).map(
                (comment) => {
                  if (comment._id === payload.commentId) {
                    return {
                      ...comment,
                      isResolved:
                        data?.data?.comment?.isResolved ?? payload.isResolved,
                    };
                  }
                  return comment;
                }
              ),
            },
          },
        };
      }
    );
  };

  const {
    mutate: resolveComment,
    isLoading: isResolving,
    isSuccess: isResolved,
    variables,
  } = useMutation({
    mutationFn: async (payload: IResolveScriptComment) => {
      // Optimistically update
      updateCommentResolve(payload);
      return BriefConfigService.resolveScriptComment(payload).catch(
        async (err) => {
          const responseBody = await err.response?.json?.();
          if (responseBody?.msg) ToastService.error(responseBody.msg);
          // Revert on error
          updateCommentResolve({
            ...payload,
            isResolved: !payload.isResolved,
          });
          throw err;
        }
      );
    },
    options: {
      skipInvalidation: true,
    },
    onSuccess: (c, data, params) => {
      updateCommentResolve(params, data);
    },
  });

  return {
    resolveComment,
    isResolving,
    isResolved,
    resolveCommentId: variables?.commentId || "",
  };
}
